import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react"
import { Stack } from "@mui/material"
import { useDebouncedCallback } from "use-debounce"

import { ConfigQuery, fetchResourceFilters, ResourceFiltersResponse } from "../../../createOrderApi"
import {
    ResourceFilterElementValue,
    ResourceFiltersSelectedItem,
    ResourceFiltersTypes,
} from "../../../createOrderTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { AddFiltersButton } from "./AddFiltersButton"
import { FilterChip } from "./FilterChip"

const TIMELINE_EXCLUDED_GROUP_KEYS = ["key_group_2", "key_group_3"] as const

const FILTER_VALUES_DEBOUNCE_MS = 400

const transformFilters = (data: ResourceFiltersResponse, isTimeline: boolean) => {
    return (data.filterGroups ?? []).flatMap((group) => {
        if (isTimeline && TIMELINE_EXCLUDED_GROUP_KEYS.includes(group.translationKey as (typeof TIMELINE_EXCLUDED_GROUP_KEYS)[number])) {
            return []
        }
        const elements = group.filterElements ?? []
        return group.translationKey === "key_group_1"
            ? elements.map((el) => ({ ...el, name: group.name }))
            : elements
    })
}

function getCommittedFilters(list: readonly ResourceFiltersSelectedItem[]): ResourceFiltersSelectedItem[] {
    return list.filter((f) => f.values.length > 0).map((f) => ({
        ...f,
        values: f.values.map((v) => ({ ...v })),
    }))
}

function sameFilterLists(
    a: readonly ResourceFiltersSelectedItem[],
    b: readonly ResourceFiltersSelectedItem[],
): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        if (a[i].identifier !== b[i].identifier) return false
        if (a[i].values.length !== b[i].values.length) return false
        for (let j = 0; j < a[i].values.length; j++) {
            if (a[i].values[j].key !== b[i].values[j].key || a[i].values[j].value !== b[i].values[j].value) {
                return false
            }
        }
    }
    return true
}

type ResourceFilterMenuProps = Readonly<{
    filters: ResourceFiltersSelectedItem[]
    setFilters: Dispatch<SetStateAction<ResourceFiltersTypes>>
    isTimeline?: boolean
    userId: number
}>

function cloneFilterRows(rows: readonly ResourceFiltersSelectedItem[]): ResourceFiltersSelectedItem[] {
    return rows.map((f) => ({
        ...f,
        values: f.values.map((v) => ({ ...v })),
    }))
}

export function ResourceFilterMenu({ filters, userId, setFilters, isTimeline = false }: ResourceFilterMenuProps) {
    const { data: resourceFiltersResult = [] } = useApiQuery(fetchResourceFilters, {
        queryName: ConfigQuery.RESOURCE_FILTERS,
        dependencies: {
            userId,
        },
        select: (data) => transformFilters(data, isTimeline),
    })

    const [activeFilter, setActiveFilter] = useState<string | null>(null)
    const chipFiltersRef = useRef<ResourceFiltersSelectedItem[]>(cloneFilterRows(filters))
    const [chipFilters, setChipFilters] = useState<ResourceFiltersSelectedItem[]>(() => cloneFilterRows(filters))

    useEffect(() => {
        chipFiltersRef.current = chipFilters
    }, [chipFilters])

    const applyParentCommitted = useCallback(
        (list: readonly ResourceFiltersSelectedItem[]) => {
            const committed = getCommittedFilters(list)
            setFilters((prev) => {
                if (sameFilterLists(prev.filters, committed)) return prev
                return { ...prev, filters: committed }
            })
        },
        [setFilters],
    )

    const debouncedApplyParentCommitted = useDebouncedCallback(() => {
        applyParentCommitted(chipFiltersRef.current)
    }, FILTER_VALUES_DEBOUNCE_MS)

    useEffect(() => {
        return () => {
            debouncedApplyParentCommitted.flush()
        }
    }, [debouncedApplyParentCommitted])

    const flushFilterValuePatches = useCallback(() => {
        debouncedApplyParentCommitted.flush()
    }, [debouncedApplyParentCommitted])

    const handleAddFilter = (filter: string) => {
        debouncedApplyParentCommitted.flush()
        setChipFilters((prev) => {
            const next = prev.some((f) => f.identifier === filter)
                ? prev.filter((f) => f.identifier !== filter)
                : [...prev, { identifier: filter, values: [] }]
            chipFiltersRef.current = next
            applyParentCommitted(next)
            return next
        })
        setActiveFilter(filter)
    }

    const updateFilterValues = useCallback(
        (identifier: string, values: ResourceFilterElementValue[]) => {
            setChipFilters((prev) => {
                const next = prev.map((f) => (f.identifier === identifier ? { ...f, values } : f))
                chipFiltersRef.current = next
                return next
            })
            debouncedApplyParentCommitted()
        },
        [debouncedApplyParentCommitted],
    )

    return (
        <Stack flexDirection="row" gap=".75rem" flexWrap="wrap" width="fit-content" alignItems="center" flex="none">
            {chipFilters.map((filter) => (
                <FilterChip
                    key={filter.identifier}
                    filterType={filter.identifier}
                    isRemovable={filter?.isRemovable}
                    onRemove={() => {
                        debouncedApplyParentCommitted.flush()
                        setChipFilters((prev) => {
                            const next = prev.filter((f) => f !== filter)
                            chipFiltersRef.current = next
                            applyParentCommitted(next)
                            return next
                        })
                    }}
                    resourceFiltersResult={resourceFiltersResult}
                    isActive={activeFilter === filter.identifier}
                    setActiveFilter={setActiveFilter}
                    initialValues={filter.values}
                    onSave={(values) => updateFilterValues(filter.identifier, values)}
                    onPopoverClose={flushFilterValuePatches}
                />
            ))}

            <AddFiltersButton
                filters={chipFilters}
                resourceFiltersResult={resourceFiltersResult}
                onAddFilter={handleAddFilter}
            />
        </Stack>
    )
}
